import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  IconButton,
  Tooltip,
  Flex,
  Spacer,
  Divider,
  Spinner,
  Textarea,
  Center,
  InputGroup,
  InputLeftElement,
  Heading,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Icon,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiFolder, FiFile, FiDownload, FiUpload, FiSearch, FiTag, FiInfo, FiUploadCloud } from 'react-icons/fi';
import axios from 'axios';

// Dummy data for knowledge bases and documents
const dummyKnowledgeBases = [
    { id: 'kb-1', name: 'General FAQs', created_at: '2023-10-01T10:00:00Z', documents: [] },
    { id: 'kb-2', name: 'Product Manuals', created_at: '2023-11-15T12:00:00Z', documents: [] },
];

const dummyDocuments = [
    { id: 'doc-1', name: 'Shipping Policy.pdf', type: 'file', created_at: '2023-10-05T14:30:00Z', metadata: { category: 'Policy', language: 'en' }, tags: ['shipping', 'policy'] },
    { id: 'doc-2', name: 'Return Process.txt', type: 'text', created_at: '2023-10-06T09:00:00Z', metadata: { category: 'Procedure' }, tags: ['returns'] },
    { id: 'doc-3', name: 'Manual_v1.2.pdf', type: 'file', created_at: '2023-11-20T11:00:00Z', metadata: { version: '1.2', product: 'XYZ' }, tags: ['manual', 'product'] },
];

const KnowledgeBase = ({ apiKey }) => {
  const [knowledgeBases, setKnowledgeBases] = useState(dummyKnowledgeBases);
  const [selectedKbId, setSelectedKbId] = useState(null);
  const [selectedKBDetails, setSelectedKBDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newKBName, setNewKBName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [textSource, setTextSource] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalDocs, setTotalDocs] = useState(0);
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const { isOpen: isAddDocumentModalOpen, onOpen: onAddDocumentModalOpen, onClose: onAddDocumentModalClose } = useDisclosure();
  const { isOpen: isMetadataModalOpen, onOpen: onMetadataModalOpen, onClose: onMetadataModalClose } = useDisclosure();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const toast = useToast();

  const chatbotClient = axios.create({
    baseURL: 'https://api.chatbot.com',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
  });
  chatbotClient.defaults.headers.post['Content-Type'] = 'application/json';
  chatbotClient.defaults.headers.get['Content-Type'] = 'application/json';
  chatbotClient.defaults.headers.delete['Content-Type'] = 'application/json';

  const fetchKnowledgeBases = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    setKnowledgeBases(dummyKnowledgeBases);
    setLoading(false);
  };

  const fetchKnowledgeBaseDetails = async (kbId) => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    const kb = dummyKnowledgeBases.find(kb => kb.id === kbId);
    if (kb) {
        const documents = dummyDocuments;
        setSelectedKBDetails({ ...kb, documents });
        setTotalDocs(documents.length);
    } else {
        setSelectedKBDetails(null);
        setTotalDocs(0);
    }
    setLoading(false);
  };

  const createKnowledgeBase = async () => {
    if (!newKBName) {
      toast({
        title: 'Name required',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    const newKb = {
        id: `kb-${Date.now()}`,
        name: newKBName,
        created_at: new Date().toISOString(),
        documents: []
    };
    setKnowledgeBases([...knowledgeBases, newKb]);
    setNewKBName('');
    onCreateModalClose();
    toast({
      title: 'Knowledge Base Created',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    setLoading(false);
  };

  const deleteKnowledgeBase = async (kbId) => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    setKnowledgeBases(knowledgeBases.filter(kb => kb.id !== kbId));
    setSelectedKbId(null);
    setSelectedKBDetails(null);
    toast({
      title: 'Knowledge Base Deleted',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    setLoading(false);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      onAddDocumentModalOpen();
    }
  };

  const handleFileSelect = (event) => {
    setSelectedFiles(Array.from(event.target.files));
  };

  const addDocuments = async () => {
    if (selectedFiles.length === 0 && !textSource) {
      toast({
        title: 'No files selected or text entered',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newDocuments = [];
      selectedFiles.forEach(file => {
        newDocuments.push({
          id: `doc-${Date.now()}-${file.name}`,
          name: file.name,
          type: 'file',
          created_at: new Date().toISOString(),
          metadata: {},
          tags: [],
        });
      });

      if (textSource) {
        newDocuments.push({
          id: `doc-${Date.now()}-text`,
          name: 'Text Content',
          type: 'text',
          created_at: new Date().toISOString(),
          metadata: {},
          tags: [],
        });
      }

      setTotalDocs(prev => prev + newDocuments.length);
      toast({
        title: 'Document(s) Added',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setSelectedFiles([]);
      setTextSource('');
      onAddDocumentModalClose();
    } catch (error) {
      toast({
        title: 'Error Adding Documents',
        description: error.message || 'Failed to add documents',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (kbId, documentId) => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    const updatedKBs = knowledgeBases.map(kb => {
        if (kb.id === kbId) {
            return { ...kb, documents: kb.documents.filter(document => document.id !== documentId) };
        }
        return kb;
    });
    setKnowledgeBases(updatedKBs);

    setSelectedKBDetails(prevDetails => {
        if (prevDetails && prevDetails.id === kbId) {
            const updatedDocs = prevDetails.documents.filter(document => document.id !== documentId);
            setTotalDocs(updatedDocs.length);
            return { ...prevDetails, documents: updatedDocs };
        }
        return prevDetails;
    });

    toast({
        title: 'Document Deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
    });

    setLoading(false);
  };

  const handleOpenMetadataModal = (document) => {
    setSelectedDocument(document);
    onMetadataModalOpen();
  };

  const handleKbSelect = (kbId) => {
    setSelectedKbId(kbId);
    fetchKnowledgeBaseDetails(kbId);
    setSearchQuery('');
  };

  const handleDocumentSearch = (e) => {
      setSearchQuery(e.target.value);
  };

  const filteredDocuments = selectedKBDetails?.documents?.filter(document =>
      document.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const filteredKnowledgeBases = knowledgeBases.filter(kb =>
      kb.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (apiKey) {
       fetchKnowledgeBases();
    } else {
       setKnowledgeBases(dummyKnowledgeBases);
    }
  }, [apiKey]);

  return (
    <Flex direction="row" gap={6}>
      <Center minH="100px">
        {loading ? (
          <Spinner size="lg" />
        ) : (
          <VStack spacing={4}>
            <Box p={4} borderWidth="1px" borderRadius="lg" shadow="md">
              <Text fontSize="xl" fontWeight="bold">
                Total Documents: {totalDocs}
              </Text>
            </Box>
            
              <FormControl>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="upload-kb-file"
                  accept=".pdf,.txt,.doc,.docx,.xls,.xlsx"
                />
                <FormLabel htmlFor="upload-kb-file" mb="0">
                  <Button as="span" colorScheme="orange" cursor="pointer">
                    <HStack>
                      <Icon as={FiUploadCloud} />
                      <Text>Upload File</Text>
                    </HStack>
                  </Button>
                </FormLabel>
              </FormControl>
           
          </VStack>
        )}
      </Center>

      <Modal isOpen={isAddDocumentModalOpen} onClose={onAddDocumentModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch" pb={4}>
              <Text fontWeight="bold">Upload Files</Text>
              <FormControl>
                <Input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  p={1}
                  height="auto"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  accept=".pdf,.txt,.doc,.docx,.xls,.xlsx"
                />
              </FormControl>
              {selectedFiles.length > 0 && (
                <Text fontSize="sm" color="gray.600">{selectedFiles.length} file(s) selected</Text>
              )}

              <Text fontWeight="bold" mt={4}>Or Add Text</Text>
              <FormControl>
                <Textarea
                  value={textSource}
                  onChange={(e) => setTextSource(e.target.value)}
                  placeholder="Enter text content..."
                  rows={6}
                />
              </FormControl>
              <Button 
                colorScheme="blue" 
                onClick={addDocuments} 
                isLoading={loading}
                isDisabled={selectedFiles.length === 0 && !textSource}
              >
                Add Document(s)
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isMetadataModalOpen} onClose={onMetadataModalClose} size="md">
          <ModalOverlay />
          <ModalContent>
              <ModalHeader>Metadata & Tags for "{selectedDocument?.name || 'Document'}"</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                  <VStack spacing={4}>
                      <Box width="100%">
                          <Text fontWeight="bold" mb={2}>Metadata:</Text>
                          <Box bg="gray.100" p={3} borderRadius="md" fontFamily="mono" fontSize="sm">
                              <pre>{JSON.stringify(selectedDocument?.metadata, null, 2)}</pre>
                          </Box>
                      </Box>
                      <Box width="100%">
                          <Text fontWeight="bold" mb={2}>Tags:</Text>
                          <Wrap spacing={2}>
                              {selectedDocument?.tags.map(tag => (
                                  <WrapItem key={tag}>
                                      <Tag size="md" colorScheme="purple">
                                          <TagLabel>{tag}</TagLabel>
                                      </Tag>
                                  </WrapItem>
                              ))}
                          </Wrap>
                      </Box>
                  </VStack>
              </ModalBody>
          </ModalContent>
      </Modal>
    </Flex>
  );
};

export default KnowledgeBase; 